import {
  type ReturnLeadReadable,
  leadsListOptions,
  leadsTypesListOptions,
  leadsTypesRetrieveOptions,
  listViewsResultListOptions
} from '@crm/api'
import {
  ActionsHandler,
  ActionsLeadType,
  ChangeFieldStatus,
  InfoPopover,
  PhoneNumbers
} from '@crm/components'
import {
  AddLeadView,
  ExportLeads,
  LeadsFilters
} from '@crm/features/leads/components'
import { useLeadView } from '@crm/features/leads/hooks'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useCheckedList } from '@crm/hooks/useCheckedList'
import { useRolePermission } from '@crm/hooks/useRolePermission'
import { useStateHandlers } from '@crm/hooks/useStateHandlers'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { paths } from '@crm/routes'
import { LeadsModalType, UserRoleCRM } from '@crm/types'
import { defaultNonPagination, getActionColumnItem } from '@crm/utils'
import {
  ConditionalText,
  DayLabel,
  TableActions,
  TableContainer,
  TagsGroup,
  TitleSection
} from '@shared/components'
import { PaginationTypes, useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { defaultFilters, getFullName, getTokenFromUrl } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Button, Checkbox, Flex, Segmented, Tooltip } from 'antd'
import {
  Add,
  Bookmark2,
  Profile2User,
  Tag as TagIcon,
  Trash
} from 'iconsax-react'
import { useMemo, useState } from 'react'

import { useLocation, useParams } from 'react-router-dom'

import type { TableColumnsType } from 'antd'

export const Leads = () => {
  const { id } = useParams()
  const navigate = useAppNavigate()

  const { t } = useLocales()
  const params = useQueryParams()
  const location = useLocation()
  const [modalType, setModalType] = useState<LeadsModalType | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const sorter = useTableSorter()

  const { segmentedValue, segmentedOptions } = useLeadView()
  const isAdmin = useRolePermission([UserRoleCRM.admin])

  const [selectedLeads, setSelectedLeads] = useStateHandlers<{
    ids: number[]
    type?: ActionsLeadType
  }>({ ids: [] })

  const queryParams = {
    ...params,
    page: Number(params.page || defaultFilters.page || 1),
    per_page: Number(
      params.per_page || params.page_size || params.perPage || 10
    )
  }

  const { data: leads, isLoading: isLeadsLoading } = useQuery(
    leadsListOptions({ query: queryParams })
  )

  const { data: leadsTypes } = useQuery(
    leadsTypesListOptions({
      query: defaultNonPagination
    })
  )

  const isViewListPage = location.pathname.includes('list-view')

  const valueId = isViewListPage ? id : undefined

  const { data: presetViews, isLoading: isPresetLoading } = useQuery({
    ...listViewsResultListOptions({
      path: { list_id: String(valueId) },
      query: queryParams
    }),
    enabled: !!valueId
  })

  const { data: leadType, isLoading: isLoadingLeadType } = useQuery({
    ...leadsTypesRetrieveOptions({
      path: { id: Number(params.lead_type) }
    }),
    enabled: !!params.lead_type
  })

  const onRow = (data: ReturnLeadReadable) => ({
    onClick: () => navigate(`/apps/crm/${getTokenFromUrl()}/leads/${data?.id}`)
  })

  const onCell = () => ({
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.MouseEvent) => e.stopPropagation()
  })

  const onCancelModal = () => {
    setModalType(null)
    setSelectedId(null)
  }

  const onActionHandler = () => setSelectedLeads({ ids: [], type: undefined })

  const {
    checkedItems: checkedLeads,
    isCheckedAllItems: isCheckedAllLeads,
    onItemCheck: onLeadCheck,
    onAllCheck
  } = useCheckedList(leads?.results.map((i) => i.id!) || [])

  const actionsColumn: TableColumnsType<ReturnLeadReadable> = useMemo(
    () => [
      {
        width: 50,
        key: 'actions',
        fixed: 'right',
        render: ({ id }) => (
          <TableActions
            options={getActionColumnItem([id], {
              checkedLeads,
              navigate,
              setSelectedLeads,
              t,
              isAdmin
            })}
          />
        )
      }
    ],
    [checkedLeads, navigate, setSelectedLeads, t, isAdmin]
  )

  const leadsColumns: TableColumnsType<ReturnLeadReadable> = [
    {
      title: <Checkbox onClick={onAllCheck} checked={isCheckedAllLeads} />,
      render: ({ id }) => (
        <Checkbox
          onChange={(e) => {
            return onLeadCheck(e.target.checked, id)
          }}
          checked={
            isCheckedAllLeads ||
            Boolean(checkedLeads.find((item) => item === id))
          }
        />
      ),
      onCell,
      align: 'center'
    },
    {
      title: t('contacts.full_name'),
      sorter: true,
      key: 'name',
      dataIndex: 'name',
      render: (_, record) => (
        <ConditionalText condition={!!record.name}>
          {record.name}
        </ConditionalText>
      )
    },
    {
      title: t('contacts.contact_name'),
      sorter: true,
      key: 'contact',
      dataIndex: 'contact',
      render: (_, record) => (
        <ConditionalText condition={!!getFullName(record.contact)}>
          {getFullName(record.contact)}
        </ConditionalText>
      )
    },
    {
      title: t('contacts.phone_number'),
      render: (_, record) => (
        <PhoneNumbers
          onClick={(e) => e.stopPropagation()}
          phoneNumbers={record?.contact?.phone_numbers}
        />
      )
    },
    {
      title: t('contacts.lead_owner'),
      render: ({ owner }) => (
        <ConditionalText condition={owner?.name}>{owner?.name}</ConditionalText>
      )
    },
    {
      title: t('created_at'),
      sorter: true,
      key: 'created_at',
      render: (_, { contact }) => <DayLabel date={contact?.created_at} />
    },
    {
      title: t('nomenclature.tags'),
      key: 'tags',
      dataIndex: 'tags',
      width: 180,
      render: (_, { tags }) => <TagsGroup tags={tags} />
    },
    ...(leadsTypes?.results?.length
      ? ([
          {
            title: t('contacts.pipeline'),
            dataIndex: ['status', 'lead_type', 'name'],
            onCell,
            render: (name) => (
              <ConditionalText condition={!!name}>{name}</ConditionalText>
            )
          }
        ] as TableColumnsType<ReturnLeadReadable>)
      : []),
    {
      title: t('contacts.status'),
      render: (_, lead) => (
        <ChangeFieldStatus lead={lead} leadType={lead.status?.lead_type?.id} />
      ),
      onCell
    },
    ...actionsColumn
  ]

  const presetColumns: TableColumnsType<ReturnLeadReadable> = useMemo(() => {
    const positionElements =
      presetViews?.fields && presetViews?.fields[0]?.order === 1
        ? presetViews?.fields
        : presetViews?.fields?.reverse()

    const columns =
      positionElements?.map(({ label, name }) => ({
        title: label,
        key: name,
        sorter: true,
        dataIndex: name
      })) || []

    return [...columns, ...actionsColumn]
  }, [presetViews, actionsColumn])

  const isLoading = isLeadsLoading || isPresetLoading

  const dataSource = id && isViewListPage ? presetViews : leads

  const columns = id && isViewListPage ? presetColumns : leadsColumns

  const hasNumericId = (value: unknown): value is { id: number } => {
    if (typeof value !== 'object' || value === null) return false
    const record = value as Record<string, unknown>

    return typeof record.id === 'number'
  }

  const checkedIds = isCheckedAllLeads
    ? (dataSource?.results ?? [])
        .map((item) => (hasNumericId(item) ? item.id : undefined))
        .filter((id): id is number => id !== undefined)
    : checkedLeads

  const onClick = () => setModalType?.('preset')

  const isKanban = location.pathname.includes('kanban')

  const leadName = leadType?.name ? ` - ${leadType?.name}` : ''

  return (
    <>
      <TitleSection
        title={`${t(`menu.contact`)} ${leadName}`}
        count={leads?.count}
        isLoading={isLeadsLoading || isLoadingLeadType}
        extraTitle={
          leadType && <InfoPopover responsible={leadType?.responsible} />
        }
      >
        <Flex justify="flex-end" gap={8}>
          <Tooltip title={t('contacts.re_assign_responsible')}>
            <Button
              disabled={!checkedIds?.length}
              icon={<Profile2User size={16} />}
              onClick={() =>
                setSelectedLeads({ ids: checkedIds, type: 'assignTo' })
              }
            />
          </Tooltip>

          <Tooltip title={t('contacts.set_tag')}>
            <Button
              disabled={!checkedIds?.length}
              icon={<TagIcon size={16} />}
              onClick={() =>
                setSelectedLeads({ ids: checkedIds, type: 'setTag' })
              }
            />
          </Tooltip>

          {isAdmin && (
            <Tooltip title={t('contacts.send_to_campaign')}>
              <Button
                disabled={!checkedIds?.length}
                icon={<Bookmark2 size={16} />}
                onClick={() =>
                  setSelectedLeads({
                    ids: checkedIds,
                    type: 'campaignsListForm'
                  })
                }
              />
            </Tooltip>
          )}

          <Tooltip title={t('contacts.delete_record')}>
            <Button
              danger
              disabled={!checkedIds?.length}
              icon={<Trash size={16} />}
              onClick={() =>
                setSelectedLeads({ ids: checkedIds, type: 'delete' })
              }
            />
          </Tooltip>

          <ExportLeads />
        </Flex>
      </TitleSection>

      <Flex vertical gap={16}>
        <TableContainer
          rowKey="id"
          rowClassName="pointer"
          onRow={onRow}
          loading={isLoading}
          data={dataSource as PaginationTypes<ReturnLeadReadable[]>}
          columns={columns}
          showSearch
          isHoverAlternative
          leftChildren={
            <LeadsFilters
              setModalType={setModalType}
              setSelectedId={setSelectedId}
            />
          }
          rightChildren={
            <Flex gap={8}>
              {isKanban && (
                <Button
                  type="primary"
                  color="primary"
                  variant="link"
                  icon={<Add size={16} />}
                  onClick={onClick}
                >
                  {t('filter.add_view')}
                </Button>
              )}

              <Button
                icon={<Add size={16} />}
                type="primary"
                onClick={() => navigate(paths.leadsCreate)}
              >
                {t('lead.add_lead')}
              </Button>

              <Segmented
                value={segmentedValue}
                onChange={(value) => navigate(value)}
                options={segmentedOptions}
              />
            </Flex>
          }
          onChange={(_, __, result) => sorter(result)}
        />
      </Flex>

      <ActionsHandler
        leads={selectedLeads.ids}
        type={selectedLeads.type}
        onClose={onActionHandler}
        onSubmit={onActionHandler}
      />

      {modalType === 'preset' && (
        <AddLeadView id={selectedId} open onCancel={onCancelModal} />
      )}
    </>
  )
}
